<script setup>
import { useI18n } from 'vue-i18n'
import moment from "moment";
import axios from '@/helpers/axios'

definePage({
  meta: {
    layout: 'default',
    role: 'admin'
  },
})

const { t } = useI18n()

const searchQuery = ref('')
const rowPerPage = ref(10)
const currentPage = ref(1)
const totalPage = ref(1)
const totalItems = ref(0)
const items = ref([])

const getData = () => {
  axios.get('/admin/satellite', {
    params: {
      q: searchQuery.value,
      perPage: rowPerPage.value,
      currentPage: currentPage.value,
    },
  }).then(response => {
    items.value = response.data.data
    totalPage.value = response.data.pages
    totalItems.value = response.data.total
  }).catch(e => {
    console.error(e)
  })
}

watchEffect(() => {
  getData()
})

watchEffect(() => {
  if (currentPage.value > totalPage.value)
    currentPage.value = totalPage.value
})

const resolveBoolVariant = (data) => {
  if (data)
    return { color: 'success', text: t('layout.yes') }

  return { color: 'error', text: t('layout.no') }
}

// 👉 Computing pagination data
const paginationData = computed(() => {
  const firstIndex = items.value.length ? (currentPage.value - 1) * rowPerPage.value + 1 : 0
  const lastIndex = items.value.length + (currentPage.value - 1) * rowPerPage.value
  
  return t('tables.showing', { firstIndex, lastIndex, totalItems: totalItems.value })
})

</script>

<template>
  <div>
    <VCard
      id="items-list"
      :title="$t('satellite.parcels')"
    >
      <VCardText class="d-flex align-center flex-wrap gap-4">
        <!-- 👉 Rows per page -->
        <div
          class="d-flex align-center"
          style="width: 150px;"
        >
          <span class="text-no-wrap me-3">{{ $t('tables.show') }}</span>
          <VSelect
            v-model="rowPerPage"
            density="compact"
            :items="[10, 20, 30, 50]"
          />
        </div>

        <VSpacer />

        <div class="d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div class="items-list-filter">
            <VTextField
              v-model="searchQuery"
              :placeholder="$t('satellite.search_parcel')"
              density="compact"
            />
          </div>
          <VBtn
            prepend-icon="tabler-plus"
            :to="{name: 'admin-satellite-new'}"
          >
            {{ $t('satellite.add_parcel') }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- SECTION Table -->
      <VTable class="text-no-wrap items-list-table">
        <!-- 👉 Table head -->
        <thead class="text-uppercase">
          <tr>
            <th scope="col">
              {{ $t('satellite.name') }}
            </th>

            <th scope="col">
              {{ $t('satellite.user') }}
            </th>

            <th scope="col">
              {{ $t('satellite.area') }}
            </th>

            <th scope="col">
              {{ $t('satellite.last_data') }}
            </th>

            <th scope="col">
              {{ $t('satellite.validated') }}
            </th>

            <th scope="col">
              {{ $t('satellite.active') }}
            </th>

            <th scope="col" />
          </tr>
        </thead>

        <!-- 👉 Table Body -->
        <tbody>
          <tr
            v-for="item in items"
            :key="item.uuid"
            style="height: 3.75rem;"
          >
            <!-- 👉 Name -->
            <td>
              <RouterLink :to="{ name: 'satellite-uuid', params: { uuid: item.uuid } }">
                {{ item.name }}
              </RouterLink>
            </td>

            <td>
              <RouterLink
                v-if="item.user"
                :to="{ name: 'admin-users-uuid', params: { uuid: item.user.uuid } }"
              >
                {{ item.user.name }}
              </RouterLink>
              <span v-else>-</span>
            </td>

            <td>
              {{ item.area }}
            </td>

            <!-- 👉 Last Data -->
            <td>
              {{ item.last_data ? moment.utc(item.last_data).local().format("Y-MM-DD HH:mm") : $t('satellite.no_data') }}
            </td>

            <td>
              <VChip
                :color="resolveBoolVariant(item.validated).color"
                label
                class="font-weight-medium"
                size="small"
              >
                {{ resolveBoolVariant(item.validated).text }}
              </VChip>
            </td>

            <td>
              <VChip
                :color="resolveBoolVariant(item.active).color"
                label
                class="font-weight-medium"
                size="small"
              >
                {{ resolveBoolVariant(item.active).text }}
              </VChip>
            </td>

            <!-- 👉 Actions -->
            <td style="width: 8rem;">
              <VTooltip :text="$t('layout.view')">
                <template #activator="{ props }">
                  <VBtn
                    v-bind="props"
                    icon
                    variant="text"
                    color="default"
                    size="x-small"
                    :to="{ name: 'satellite-uuid', params: { uuid: item.uuid } }"
                  >
                    <VIcon
                      :size="22"
                      icon="tabler-eye"
                    />
                  </VBtn>
                </template>
              </VTooltip>
            </td>
          </tr>
        </tbody>

        <!-- 👉 table footer  -->
        <tfoot v-show="!items.length">
          <tr>
            <td
              colspan="8"
              class="text-center text-body-1"
            >
              {{ $t('satellite.no_parcels') }}
            </td>
          </tr>
        </tfoot>
      </VTable>
      <!-- !SECTION -->

      <VDivider />

      <!-- SECTION Pagination -->
      <VCardText class="d-flex align-center flex-wrap gap-4 py-3">
        <!-- 👉 Pagination meta -->
        <span class="text-sm text-disabled">
          {{ paginationData }}
        </span>

        <VSpacer />

        <!-- 👉 Pagination -->
        <VPagination
          v-model="currentPage"
          size="small"
          :total-visible="5"
          :length="totalPage"
        />
      </VCardText>
      <!-- !SECTION -->
    </VCard>
  </div>
</template>

<style lang="scss">
#items-list {
  .items-list-actions {
    inline-size: 8rem;
  }

  .items-list-filter {
    inline-size: 12rem;
  }
}
</style>
